import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

const initialState = {
  meetings: [],
  loading: false,
  error: null,
  success: null,
  searchTerm: "",
  statusFilter: "all",
  page: 0,
  rowsPerPage: 100,
  totalCount: 0,
  expandedMeetingId: null,
  hasNotificationsFilter: false,
  needsPayoutFilter: false,
};

export const fetchAdminMeetings = createAsyncThunk(
  "adminMeetings/fetchMeetings",
  async (params, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams({
        page: params.page || 0,
        limit: params.limit || 100,
        searchTerm: params.searchTerm || "",
        statusFilter: params.statusFilter || "all",
        hasNotificationsFilter: params.hasNotificationsFilter
          ? "true"
          : "false",
        payoutFilter: params.needsPayoutFilter ? "needs_payout" : "all",
        dateFrom: params.dateFrom || "",
        dateTo: params.dateTo || "",
      });

      const response = await fetch(`/api/admin/meeting?${queryParams}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to fetch meetings");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const findMeetingPage = createAsyncThunk(
  "adminMeetings/findMeetingPage",
  async (params, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams({
        meetingId: params.meetingId,
        limit: params.limit || 100,
        searchTerm: params.searchTerm || "",
        statusFilter: params.statusFilter || "all",
      });

      const response = await fetch(
        `/api/admin/meeting/find-page?${queryParams}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to find meeting page");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const resendMeetingEmail = createAsyncThunk(
  "adminMeetings/resendMeetingEmail",
  async (params, { rejectWithValue }) => {
    try {
      const response = await fetch(`/api/admin/meeting/resend-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          meetingId: params.meetingId,
          emailType: params.emailType,
          recipient: params.recipient,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || errorData.error || "Failed to resend email",
        );
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

const adminMeetingSlice = createSlice({
  name: "adminMeetings",
  initialState,
  reducers: {
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
    },
    setStatusFilter: (state, action) => {
      state.statusFilter = action.payload;
    },
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setRowsPerPage: (state, action) => {
      state.rowsPerPage = action.payload;
    },
    setExpandedMeetingId: (state, action) => {
      state.expandedMeetingId = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
    setHasNotificationsFilter: (state, action) => {
      state.hasNotificationsFilter = action.payload;
      state.page = 0;
    },
    setNeedsPayoutFilter: (state, action) => {
      state.needsPayoutFilter = action.payload;
      state.page = 0;
    },
    clearAllCache: (state) => {
      state.meetings = [];
      state.totalCount = 0;
    },
    updateMeetingInList: (state, action) => {
      const updatedMeeting = action.payload;
      const index = state.meetings.findIndex(
        (meeting) => meeting.id === updatedMeeting.id,
      );
      if (index !== -1) {
        state.meetings[index] = updatedMeeting;
      }
    },
    removeMeetingFromList: (state, action) => {
      const meetingId = action.payload;
      state.meetings = state.meetings.filter(
        (meeting) => meeting.id !== meetingId,
      );
      state.totalCount = Math.max(0, state.totalCount - 1);

      if (state.expandedMeetingId === meetingId) {
        state.expandedMeetingId = null;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminMeetings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminMeetings.fulfilled, (state, action) => {
        state.loading = false;
        state.meetings = action.payload.meetings || [];
        state.totalCount = action.payload.totalCount || 0;
        state.error = null;
      })
      .addCase(fetchAdminMeetings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(findMeetingPage.pending, (state) => {
        state.loading = true;
      })
      .addCase(findMeetingPage.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(findMeetingPage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(resendMeetingEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resendMeetingEmail.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.message || "Email resent successfully";
      })
      .addCase(resendMeetingEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setSearchTerm,
  setStatusFilter,
  setPage,
  setRowsPerPage,
  setExpandedMeetingId,
  clearError,
  clearSuccess,
  setHasNotificationsFilter,
  setNeedsPayoutFilter,
  updateMeetingInList,
  removeMeetingFromList,
  clearAllCache,
} = adminMeetingSlice.actions;

export default adminMeetingSlice.reducer;
